import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {CaretDown, CaretUp, ClockCounterClockwise, FileText, PencilSimple, Power} from '../../icons';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL, FACEBOOK_CHANNEL} from '../../channels/channels';
import {formatCompactMoney, formatFullMoney, formatGoalValue, parsePanelDate, spendPct} from './panelFormatters';
import {LineBudgetCheck, LineGoalChip, LineProjection, LineSpendBar} from './LineVisuals';
import {lineOffersSuggestion} from './economyFormatters';
import {UncoveredSpendChip} from '../../elements/UncoveredSpendChip';
import {useLineUncoveredSpend} from '../../elements/useLineUncoveredSpend';
import {ruleLeakOf} from '../../elements/uncoveredSpend';
import './LineGoalEconomicsSuggestion.scss';

// GOTCHA: Meta se representa como 'FACEBOOK' en todo el FE (`channels.js`). Un 'META' crudo cae por
// todos los `if` de ChannelLogo y no renderiza NADA, sin error.
function normalizeChannel(channel) {
    return channel === 'META' ? FACEBOOK_CHANNEL : channel;
}

/**
 * Los canales que la fila puede dibujar: normalizados y sólo los que `ChannelLogo` conoce. Exportado
 * porque la card mide con esto cuánto ancho reservar para los logos (el máximo del bloque): tienen
 * que contar lo mismo, o la reserva no coincide con lo que se dibuja.
 */
export function lineChannels(line) {
    return ((line && line.channels) || [])
        .map(normalizeChannel)
        .filter((channel) => CHANNEL_INFO_BY_CHANNEL[channel]);
}

// Cuántas campañas quedan sin nombrar en el preview (el backend manda hasta 3).
function remainingCampaigns(campaigns) {
    const total = Number(campaigns.total_count || 0);
    const shown = (campaigns.preview_names || []).length;
    return Math.max(0, total - shown);
}

// `dd/mm` para el rango de una línea con período propio. A mano y no con `toLocaleDateString`: el
// `2-digit` depende del ICU del entorno (Node lo pinta `10/7`), mismo criterio que `MemoryCard`.
function shortDate(value) {
    const date = parsePanelDate(value);
    if (!date) return null;
    const pad = (number) => String(number).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

/**
 * Una línea de control del Panel (`panel.lines[i]`).
 *
 * La fila cuenta una sola historia: cuánto va gastado contra lo que se esperaba a hoy, hacia dónde
 * va a cerrar y si el objetivo está en ritmo. Todo lo demás (presupuesto y período, campañas
 * incluidas, conversión que se sigue) aparece al tocar la fila, porque es contexto de configuración
 * y no de seguimiento diario: mostrarlo siempre convertiría la card en una tabla ilegible.
 *
 * **Tocar CUALQUIER parte de la fila la expande** (card bBcBPqGg, 23/09). Hasta esa fecha sólo lo
 * hacía un chevron de 26px contra el borde, y la cabecera tenía además un lápiz (drawer lateral), un
 * `⋮` (menú) y un reloj (historial): cuatro gestos de 26px en fila. Pablo: «me parece medio raro
 * tener este menú de hamburguesa», «esta info ya la tengo acá». Ahora las acciones son TRES y
 * viven al pie del detalle, con su nombre escrito: Editar (la línea completa, con sus solapas),
 * Ver historial y Desactivar. Eliminar y «Fuentes de datos» quedan en la vista de todas las líneas
 * y en la edición completa (decisión de fede, 24/09).
 *
 * El chevron sigue siendo un `<button aria-expanded>`: es el control que alcanza el teclado. El
 * click en el resto de la fila es un atajo de mouse que no reemplaza a ese botón.
 */
export function ControlLineRow({line, isLatestAlert, latestAlertLabel, clientId, onDefineGoal,
                               onDeactivate, finished, lineEconomy = null}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const toggle = () => setExpanded((open) => !open);
    // Una línea terminada ya no tiene período en curso: no hay nada que la regla esté dejando afuera hoy. La
    // fila muestra sólo la fuga de SU regla (`ruleLeakOf`); lo que no cuenta ninguna línea en una cuenta vive en
    // «Inversión sin control», adonde lleva la tarjeta gris de la card (UD-5).
    const uncoveredSpend = ruleLeakOf(useLineUncoveredSpend(finished ? null : line));

    /**
     * El click en la fila expande, salvo que haya caído en algo que ya tiene su propio gesto (el chip
     * de objetivo, las acciones, cualquier link, el ⓘ de la proyección) o que el usuario esté
     * seleccionando texto — copiar el nombre de una línea no puede abrirla y cerrarla.
     *
     * El DETALLE abierto tampoco pliega (C-LINEAS-01 del review): es texto para leer y copiar —el
     * cursor ahí es el de texto—, y un doble clic para seleccionar una palabra salía con la selección
     * todavía vacía en el primer `click` y cerraba la fila. Se pliega desde la cabecera, los números
     * o el chevron.
     */
    const handleRowClick = (event) => {
        if (event.target.closest
            && event.target.closest('a, button, .info-icon-container, .pnl-line__detail')) return;
        const selection = window.getSelection && window.getSelection();
        if (selection && String(selection).length > 0) return;
        toggle();
    };

    const budget = line.budget || {};
    const projection = line.projection || {};
    const goal = line.goal || {};
    const conversion = line.conversion || {};
    const currency = line.currency;

    // Un presupuesto en 0 es lo mismo que no tenerlo para esta fila: no hay contra qué medir el
    // consumo, así que va por la misma rama que el `allocated: null` del contrato.
    const hasBudget = Number(budget.allocated) > 0;
    const pct = spendPct(budget.spend, budget.allocated);

    const spendText = formatCompactMoney(currency, budget.spend);
    const allocatedText = formatCompactMoney(currency, budget.allocated);

    const renderChannels = () => {
        const channels = lineChannels(line);
        if (!channels.length) return null;

        return (
            <span className="pnl-line__chans">
                {channels.map((channel) => (
                    <ChannelLogo key={channel} channel={channel} height={18} width={18}
                                 hspace={0} vspace={0} marginLeft={0} padding={0}/>
                ))}
            </span>
        );
    };

    // El chip de objetivo, la barra y la proyección son objetos COMPARTIDOS con la vista de todas
    // las líneas (`LineVisuals.js`): el mismo dato tiene que pintarse igual en las dos pantallas.

    // El presupuesto va primero y en negrita, y el gasto pasa a ser el dato de apoyo (feedback de
    // Pablo, card OBCWCDfp). No es sólo el avance del mes: es un SETTING de la línea —lo que la
    // agencia se comprometió a invertir y lo que alimenta a la IA—, y leerlo tenía que costar cero.
    // El consumo no se pierde de vista: la barra de arriba es su representación principal, y el
    // porcentaje sigue al lado del monto invertido.
    const renderNums = () => (
        <div className="pnl-line__nums">
            <span className="pnl-line__spend">
                {hasBudget ? (
                    <>
                        {t('client_panel.line_budget_label', 'Presupuesto')}
                        {' '}
                        <strong>{allocatedText || '—'}</strong>
                        {' · '}
                        {t('client_panel.line_spend_rest', 'invertido {{spend}} · {{pct}}%', {
                            spend: spendText || '—',
                            pct: pct === null ? '—' : pct,
                            interpolation: {escapeValue: false},
                        })}
                    </>
                ) : (
                    <>
                        <span className="pnl-line__muted">
                            {t('client_panel.line_no_budget', 'Sin presupuesto')}
                        </span>
                        {' · '}
                        <strong>{spendText || '—'}</strong>
                    </>
                )}
            </span>

            <span className="pnl-line__right">
                <LineProjection projection={projection} long budget={budget} currency={currency}
                                alertLevel={line.budget_alert_level}/>
            </span>
        </div>
    );

    /**
     * La alerta viva del cliente se ancla en SU línea: si no, el KPI dice "TikTok sin gasto" y la
     * fila de TikTok no muestra nada raro.
     *
     * Va en su PROPIO renglón, debajo de los números, y no pegada a la proyección (QA-VIS-02): una
     * alerta es una frase entera, y al lado de «Proyecta gastar…» empujaba la proyección a otro
     * renglón alineada a la izquierda — la misma columna cambiaba de lado según la fila tuviera
     * alerta o no.
     */
    const renderAlert = () => {
        if (!isLatestAlert || !latestAlertLabel) return null;
        return <div className="pnl-line__alert">{latestAlertLabel}</div>;
    };

    /**
     * «No cuenta N campañas · monto» (caso Kendal): la inversión que la regla de «CÓMO SE ELIGEN» deja
     * afuera y no cuenta ninguna otra línea. Es el mismo chip que la fila del Tablero, y va en su propio
     * renglón por lo mismo que la alerta (QA-VIS-02): es una frase, y al lado de los números los empujaba a
     * otro renglón. Va SIN expandir la fila: si hubiera que abrirla para verlo, no lo vería nadie.
     */
    const renderUncoveredSpend = () => {
        const accounts = ((line.campaigns || {}).selection || []).map((entry) => ({
            account_id: entry.account_id,
            channel: entry.channel,
            name: entry.account_name,
        }));
        return <UncoveredSpendChip uncoveredSpend={uncoveredSpend} accounts={accounts} businessUnitId={line.id}
                                   rowClassName="usp-row -panel"/>;
    };

    /**
     * El objetivo que sugiere la economía del negocio (bloque `economy.lines[].goal`), cuando el
     * backend lo calculó y NO coincide con el que tiene la línea. «Aplicar» abre el modal de
     * objetivo —nada se aplica solo—, donde está el porqué y el botón que escribe.
     *
     * Va en su PROPIO renglón, debajo de la cabecera, y no pegado al chip del objetivo: nombre +
     * objetivo + sugerencia no entran en la cabecera de una card de ~760 px y el que se achica es el
     * nombre de la línea. Una línea terminada no lo ofrece, igual que «Definir objetivo».
     */
    const renderEconomySuggestion = () => {
        if (finished || !onDefineGoal || !lineOffersSuggestion(lineEconomy)) return null;
        const derived = lineEconomy.derived || {};
        const cap = formatCompactMoney(lineEconomy.currency || currency, derived.spend_cap);
        // Sin tope (menos de 30 días de histórico, lo común en una línea nueva) el chip no dice
        // «tope —»: dice sólo el sugerido.
        return (
            <div className="pnl-line__suggest">
                <button type="button" className="eco-suggest" onClick={onDefineGoal}
                        data-testid="eco-line-suggest">
                    <span className="eco-suggest__txt">
                        {t(cap ? 'client_panel.economy.line.suggested' : 'client_panel.economy.line.suggested_no_cap', {
                            roas: formatGoalValue(lineEconomy.goal.suggested),
                            cap,
                            interpolation: {escapeValue: false},
                        })}
                    </span>
                    <span className="eco-suggest__apply">{t('client_panel.economy.line.apply')}</span>
                </button>
            </div>
        );
    };

    /**
     * Con qué criterio entró lo que se mide de UNA cuenta: la cuenta entera, una lista elegida a
     * mano, o una condición sobre el nombre.
     *
     * No es un detalle de configuración: cambia qué va a pasar mañana. Con "toda la cuenta" una
     * campaña nueva entra sola; con una lista, no entra hasta que alguien la agregue. "13 DE 13" no
     * distingue esos dos mundos, y era lo único que decía la fila.
     */
    const selectionLabel = (entry) => {
        const level = entry.level;
        if (entry.mode === 'ALL') {
            if (level === 'ANALYTICS') return t('client_panel.line_selection_all_analytics', 'todo el tráfico');
            if (level === 'ECOMMERCE') return t('client_panel.line_selection_all_ecommerce', 'todos los canales de la tienda');
            return t('client_panel.line_selection_all', 'toda la cuenta');
        }
        if (entry.mode === 'LIST') {
            if (level === 'ADGROUP') return t('client_panel.line_selection_list_adgroup', 'grupos de anuncios elegidos a mano');
            if (level === 'ANALYTICS') return t('client_panel.line_selection_list_analytics', 'fuentes y medios elegidos a mano');
            if (level === 'ECOMMERCE') return t('client_panel.line_selection_list_ecommerce', 'canales elegidos a mano');
            return t('client_panel.line_selection_list', 'campañas elegidas a mano');
        }
        if (entry.mode !== 'CONDITION') return null;

        // La condición se muestra con los textos REALES: es lo que hace que el usuario entienda por
        // qué entró una campaña y no otra. Sin ningún texto cargado la condición no filtra nada, y
        // decir "por condición" a secas no explicaría el conteo.
        const includes = entry.include_containing;
        const excludes = entry.exclude_containing;
        if (includes && excludes) {
            return t('client_panel.line_selection_condition_both',
                'las que contienen «{{includes}}» y no «{{excludes}}»',
                {includes, excludes, interpolation: {escapeValue: false}});
        }
        if (includes) {
            return t('client_panel.line_selection_condition_include', 'las que contienen «{{includes}}»',
                {includes, interpolation: {escapeValue: false}});
        }
        if (excludes) {
            return t('client_panel.line_selection_condition_exclude', 'las que no contienen «{{excludes}}»',
                {excludes, interpolation: {escapeValue: false}});
        }
        return t('client_panel.line_selection_condition', 'por condición sobre el nombre');
    };

    // Con UNA cuenta el criterio va pegado al título (es de esa cuenta y no hay ambigüedad); con
    // varias, cada una dice el suyo, porque pueden ser distintos entre sí.
    const renderSelection = () => {
        if (selection.length !== 1) return null;
        return <span className="pnl-exp__how">{` · ${selection[0].label}`}</span>;
    };

    const renderSelectionList = (entries) => {
        if (entries.length < 2) return null;
        return (
            <div className="pnl-exp__col">
                <span className="pnl-exp__title">
                    {t('client_panel.line_selection_title', 'CÓMO SE ELIGEN')}
                </span>
                <span className="pnl-exp__rows">
                    {entries.map((entry, index) => (
                        <span className="pnl-exp__row" key={entry.account_id || index}>
                            {/* El nombre de una cuenta puede no haber llegado nunca del sync
                                (`AdAccount._name` es nullable): sin fallback la fila arrancaba con
                                un " · toda la cuenta" huérfano, sin decir de qué cuenta habla. */}
                            <span className="pnl-exp__acct">
                                {entry.account_name || entry.account_id}
                            </span>
                            {` · ${entry.label}`}
                        </span>
                    ))}
                </span>
            </div>
        );
    };

    // Cada cuenta con su criterio YA traducido. Se filtra por el label y no por `entry.mode`: un
    // modo que el backend mande y este archivo todavía no sepa nombrar es tan invisible como uno
    // vacío, y filtrando por `mode` la columna "CÓMO SE ELIGEN" se dibujaba con encabezado y sin
    // una sola fila debajo.
    const selection = ((line.campaigns || {}).selection || [])
        .map((entry) => ({...entry, label: selectionLabel(entry)}))
        .filter((entry) => entry.label);

    const renderCampaigns = () => {
        const campaigns = line.campaigns;
        if (!campaigns) return null;

        const rest = remainingCampaigns(campaigns);
        return (
            <div className="pnl-exp__col">
                <span className="pnl-exp__title">
                    {t('client_panel.line_campaigns', 'CAMPAÑAS ({{active}} DE {{total}})', {
                        active: campaigns.active_count,
                        total: campaigns.total_count,
                    })}
                    {renderSelection()}
                </span>
                <span className="pnl-exp__chips">
                    {(campaigns.preview_names || []).map((name) => (
                        <span className="pnl-chip" key={name}>{name}</span>
                    ))}
                    {rest > 0 && (
                        <span className="pnl-chip -more">
                            {t('client_panel.line_campaigns_more', '+ {{count}} más', {count: rest})}
                        </span>
                    )}
                </span>
            </div>
        );
    };

    // El presupuesto como SETTING (monto completo + período), no como avance: el avance ya está en la
    // barra y los números de arriba. Es lo primero que se configura de una línea y lo que Pablo pidió
    // ver sin abrir un panel aparte.
    const renderBudget = () => {
        const period = line.period || {};
        let periodText = t('client_panel.lines_view_period_monthly', 'Mensual');
        if (period.type === 'TEMPORARY') {
            const from = shortDate(period.from);
            const to = shortDate(period.to);
            periodText = from && to
                ? `${from} – ${to}`
                : t('client_panel.lines_view_period_own', 'Período propio');
        }
        const amount = hasBudget ? formatFullMoney(currency, budget.allocated) : null;

        return (
            <div className="pnl-exp__col">
                <span className="pnl-exp__title">
                    {t('client_panel.line_budget_title', 'PRESUPUESTO')}
                </span>
                <span className="pnl-exp__rows">
                    <span className="pnl-exp__value">
                        {amount || t('client_panel.line_no_budget', 'Sin presupuesto')}
                        {` · ${periodText}`}
                    </span>
                </span>
            </div>
        );
    };

    // Las tres acciones de la línea, con el verbo escrito. Van al pie del DETALLE y no en la cabecera
    // porque ahí compiten con el gesto de expandir: se llega a ellas mirando la configuración, que es
    // el momento en que se decide editarla.
    const renderActions = () => (
        <div className="pnl-line__acts">
            <Link className="pnl-btn pnl-btn--ghost pnl-btn--sm" to={`/business-unit/${line.id}/`}>
                <PencilSimple size={14} weight="bold"/>
                {t('client_panel.line_action_edit', 'Editar')}
            </Link>
            {/* El historial de ESTA línea: sin `?business_unit=` la pantalla abre la primera línea
                del cliente, que casi nunca es la que se tocó. Sin cliente no hay ruta. */}
            {clientId && (
                <Link className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                      to={`/client/${clientId}/history?business_unit=${line.id}`}>
                    <ClockCounterClockwise size={14} weight="bold"/>
                    {t('client_panel.line_action_history', 'Ver historial')}
                </Link>
            )}
            {/* La fila no escribe: avisa, y el shell confirma y llama a `lineManageActions`. */}
            {onDeactivate && (
                <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                        onClick={() => onDeactivate(line)}>
                    <Power size={14} weight="bold"/>
                    {t('client_panel.line_action_deactivate', 'Desactivar')}
                </button>
            )}
        </div>
    );

    const renderConversion = () => {
        if (!conversion.label) return null;

        const sourceLabel = conversion.source === 'inherited'
            ? t('client_panel.line_conversion_inherited', 'heredada del cliente')
            : conversion.source === 'own' ? t('client_panel.line_conversion_own', 'propia') : null;

        return (
            <div className="pnl-exp__col">
                <span className="pnl-exp__title">
                    {t('client_panel.line_conversion_title', 'CONVERSIÓN A SEGUIR')}
                </span>
                <span className="pnl-exp__chips">
                    <span className="pnl-chip -conv">{conversion.label}</span>
                    {sourceLabel && <span className="pnl-exp__source">{` · ${sourceLabel}`}</span>}
                </span>
            </div>
        );
    };

    const expandLabel = expanded
        ? t('client_panel.line_collapse', 'Ocultar el detalle de la línea')
        : t('client_panel.line_expand', 'Ver el detalle de la línea');

    return (
        <div className={`pnl-line${expanded ? ' -open' : ''}${finished ? ' -finished' : ''}`}
             onClick={handleRowClick}>
            <div className="pnl-line__top">
                {renderChannels()}
                {/* Nombre e ícono del media plan van en UN grupo: el que crece o se encoge en la
                    fila angosta es el grupo, y el ícono queda siempre pegado al nombre. Sueltos, a
                    720px el nombre crecía y empujaba al ícono contra el chevron (QA-VIS-03). */}
                <span className="pnl-line__title">
                    {/* El nombre ya no es un link: tocar la fila la expande, y la navegación a la
                        línea completa es «Editar», nombrada, al pie del detalle. */}
                    <span className="pnl-line__name">{line.name}</span>
                    {/* Las líneas que nacieron del media plan se distinguen de las creadas a mano
                        (card gW32VovP): sus cambios llegan desde la planilla. `=== true` porque un
                        backend viejo no manda el campo, y eso no es «a mano». */}
                    {line.from_media_plan === true && (
                        <span className="info-icon-container pnl-line__mp"
                              aria-label={t('client_panel.line_from_media_plan_aria', 'Sincronizada con el media plan')}>
                            <FileText size={15} weight="bold"/>
                            <span className="warning-tooltip">
                                <span className="warning-tooltip-body">
                                    <span className="warning-tooltip-text">
                                        {t('client_panel.line_from_media_plan',
                                            'Sincronizada con el media plan: sus cambios llegan desde la planilla.')}
                                    </span>
                                </span>
                            </span>
                        </span>
                    )}
                </span>
                {/* "Definir objetivo" abre el modal de objetivo y NADA más (card 6zUFUONB). Es un
                    <button>, así que su click no expande la fila.
                    En una línea TERMINADA no se ofrece (QA-VIS-07): el período cerró y no hay ritmo
                    contra el cual evaluar un objetivo nuevo. Un objetivo que ya tenía sí se muestra:
                    es parte de cómo cerró. */}
                {!(finished && !(goal && goal.defined)) && (
                    <LineGoalChip goal={goal} lineId={line.id} onDefine={finished ? undefined : onDefineGoal}/>
                )}
                {/* El chevron es el ÚNICO gesto de la cabecera, y el que alcanza el teclado. Va en
                    el cluster con `margin-left: auto` (Flexbox §8.1: un solo `auto` por fila). */}
                <span className="pnl-line__actions">
                    <button
                        type="button"
                        className="pnl-line__chev"
                        aria-expanded={expanded}
                        title={expandLabel}
                        aria-label={expandLabel}
                        onClick={toggle}
                    >
                        {expanded ? <CaretUp size={14} weight="bold"/> : <CaretDown size={14} weight="bold"/>}
                    </button>
                </span>
            </div>

            {renderEconomySuggestion()}

            <LineSpendBar line={line}/>
            {renderNums()}
            {/* Con 3 veces el presupuesto o más: «¿El presupuesto está al día?» (D6, `LineVisuals`).
                En su propio renglón y no pegado al porcentaje, por lo mismo que la alerta de abajo
                (QA-VIS-02): al lado del gasto empujaba la proyección a otro renglón. */}
            <LineBudgetCheck line={line}/>
            {renderUncoveredSpend()}
            {renderAlert()}

            {expanded && (
                <div className="pnl-line__detail">
                    <div className="pnl-line__exp">
                        {renderBudget()}
                        {renderCampaigns()}
                        {renderSelectionList(selection)}
                        {renderConversion()}
                    </div>
                    {renderActions()}
                </div>
            )}
        </div>
    );
}
